import { useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Download, FileClock, Trash2 } from 'lucide-react';
import {
  type StoredWebCapture,
  downloadWebCapture,
  useDeleteWebCapture,
  useDeviceWebCaptures,
} from '@/hooks/useDeviceWebCaptures';
import { useConfirm } from '@/hooks/useConfirm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface DeviceWebCapturesProps {
  deviceId: string;
}

const MODE_LABEL: Record<string, string> = {
  guided: 'You walked us through it',
  assisted_crawl: 'Automatic follow-up',
};

/** Plural-aware count, so nothing reads "1 requests". */
function count(n: number, singular: string): string {
  return `${String(n)} ${singular}${n === 1 ? '' : 's'}`;
}

/**
 * Recordings of this device's web UI that were shared with support.
 *
 * Sits under the live Web UI proxy rather than in its own tab: the natural
 * moment to wonder what you have already sent is right after being asked to
 * send another one.
 *
 * Every row states when it will be deleted. That date comes from the server's
 * own retention window rather than being restated here, because a number the
 * UI invents is a promise nothing enforces.
 */
export default function DeviceWebCaptures({ deviceId }: DeviceWebCapturesProps) {
  const { data, isLoading, isError } = useDeviceWebCaptures(deviceId);
  const deleteCapture = useDeleteWebCapture(deviceId);
  const { confirm, confirmDialog } = useConfirm();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleDownload = async (capture: StoredWebCapture) => {
    setBusyId(capture.id);
    setActionError(null);
    try {
      await downloadWebCapture(deviceId, capture);
    } catch {
      setActionError('Could not download that capture.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (capture: StoredWebCapture) => {
    const confirmed = await confirm({
      title: 'Delete this capture?',
      message:
        'This deletes our copy of the recording. It cannot be undone, and support will no longer be able to use it to add support for this device.',
      confirmText: 'Delete',
      isDanger: true,
    });
    if (!confirmed) return;
    setBusyId(capture.id);
    setActionError(null);
    try {
      await deleteCapture.mutateAsync(capture.id);
    } catch {
      setActionError('Could not delete that capture.');
    } finally {
      setBusyId(null);
    }
  };

  // No skeleton: this section is empty for nearly every device, so a
  // placeholder that almost always resolves to nothing is a flash of
  // furniture rather than a hint that something is coming.
  if (isLoading) {
    return null;
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load shared captures.</AlertDescription>
      </Alert>
    );
  }

  const captures = data?.captures ?? [];

  // Nothing shared is the normal state for nearly every device, and this
  // section sits under the terminal recordings — an empty panel stacked under
  // another empty panel says less than no panel at all. What sharing means is
  // explained where it is offered, not here.
  if (captures.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <FileClock className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Shared with support</span>
        <span className="text-xs text-muted-foreground">
          Deleted automatically after {count(data?.retentionDays ?? 0, 'day')}
        </span>
      </div>

      {actionError ? (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      {captures.map((capture) => {
        const pages = capture.pageUrls ?? [];
        const removed = capture.redaction?.total ?? 0;
        const isExpanded = expanded === capture.id;
        const busy = busyId === capture.id;
        return (
          <div key={capture.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">
                    {format(new Date(capture.startedAt), 'PP p')}
                  </span>
                  <Badge className="border-0 bg-muted font-normal text-muted-foreground">
                    {MODE_LABEL[capture.captureMode] ?? capture.captureMode}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {count(pages.length, 'page')}, {count(capture.exchangeCount, 'request')}
                  {removed > 0 ? `, ${count(removed, 'secret')} removed before sending` : ''}
                  {' · shared '}
                  {formatDistanceToNow(new Date(capture.createdAt), { addSuffix: true })}
                </p>
                <p className="text-xs text-muted-foreground">
                  Deleted {formatDistanceToNow(new Date(capture.expiresAt), { addSuffix: true })}
                  {' ('}
                  {format(new Date(capture.expiresAt), 'PP')})
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={busy}
                  onClick={() => { void handleDownload(capture); }}
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-sev-1 hover:text-sev-1"
                  disabled={busy}
                  onClick={() => { void handleDelete(capture); }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            </div>

            {capture.note ? (
              // The one part of a volunteered capture that says what it is
              // for. Given room accordingly, rather than tucked in with the
              // counts.
              <p className="mt-2 border-l-2 border-border-subtle pl-3 text-sm italic text-muted-foreground">
                “{capture.note}”
              </p>
            ) : null}

            {pages.length > 0 ? (
              <div className="mt-2">
                <button
                  type="button"
                  className="text-xs text-brand underline underline-offset-2"
                  onClick={() => { setExpanded(isExpanded ? null : capture.id); }}
                >
                  {isExpanded ? 'Hide' : 'Show'} the {count(pages.length, 'page')} this covers
                </button>
                {isExpanded ? (
                  <ul className="mt-2 max-h-40 overflow-y-auto rounded border border-border-subtle bg-background p-2">
                    {pages.map((url) => (
                      <li key={url} className="truncate font-mono text-xs text-muted-foreground">
                        {url}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {confirmDialog}
    </div>
  );
}
